import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client";
import { PaginatedTable } from "../components/PaginatedTable";
import {
  listCustomers, AUDIT_ACTIONS, AUDIT_LABELS,
  type AuditEntry, type AuditAction,
} from "../api/portal";

interface AuditResponse {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

const LIMIT = 50;

/** Render the before/after JSON as something an operator can read at a
 *  glance, rather than dumping raw JSON into a table cell. */
function describe(entry: AuditEntry): string {
  const d = entry.details ?? {};
  const parts: string[] = [];
  for (const [key, value] of Object.entries(d)) {
    if (value && typeof value === "object" && "from" in (value as object)) {
      const v = value as { from: unknown; to: unknown };
      parts.push(`${key}: ${String(v.from)} → ${String(v.to)}`);
    } else {
      parts.push(`${key}: ${Array.isArray(value) ? value.join(", ") : String(value)}`);
    }
  }
  return parts.join(" · ");
}

export function AuditPage() {
  const [clientId, setClientId] = useState("");
  const [action, setAction] = useState<AuditAction | "">("");
  const [offset, setOffset] = useState(0);

  const { data: customerData } = useQuery({ queryKey: ["customers"], queryFn: listCustomers });

  const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
  if (clientId) params.set("client_id", clientId);
  if (action) params.set("action", action);

  const { data, isLoading } = useQuery({
    queryKey: ["audit", clientId, action, offset],
    queryFn: () => apiFetch<AuditResponse>(`/portal/audit?${params.toString()}`),
  });

  const reset = (fn: () => void) => { fn(); setOffset(0); };

  return (
    <div>
      <h2>Audit log</h2>
      <p className="dim">
        Every change staff make to a customer or to staff accounts. Reads aren't
        recorded — browsing a customer's data leaves no entry here.
      </p>

      <div style={{ display: "flex", gap: 8, margin: "12px 0" }}>
        <select value={clientId} onChange={(e) => reset(() => setClientId(e.target.value))}>
          <option value="">All customers</option>
          {(customerData?.customers ?? []).map((c) => (
            <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
          ))}
        </select>
        <select value={action}
                onChange={(e) => reset(() => setAction(e.target.value as AuditAction | ""))}>
          <option value="">All actions</option>
          {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{AUDIT_LABELS[a]}</option>)}
        </select>
      </div>

      {isLoading && <p>Loading…</p>}
      {!isLoading && (data?.total ?? 0) === 0 && (
        <p className="empty">Nothing recorded for this filter.</p>
      )}

      {(data?.total ?? 0) > 0 && (
        <PaginatedTable
          columns={[
            { header: "When", render: (r: AuditEntry) => new Date(r.created_at).toLocaleString() },
            { header: "Who", render: (r: AuditEntry) => r.actor_email },
            { header: "Action", render: (r: AuditEntry) => AUDIT_LABELS[r.action] ?? r.action },
            { header: "Customer", render: (r: AuditEntry) => r.client_id ?? "—" },
            { header: "Details", render: (r: AuditEntry) => describe(r) },
          ]}
          rows={data?.items ?? []}
          total={data?.total ?? 0}
          limit={LIMIT}
          offset={offset}
          onPageChange={setOffset}
          rowKey={(r: AuditEntry) => r.audit_id}
        />
      )}
    </div>
  );
}
